import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchNotificationSummary,
  fetchNotificationsPage,
  markAllNotificationsRead,
  markNotificationRead,
} from './notifications-api';

/** Refresh interval of the bell badge (spec 50 §6.5: "refreshed every 60 s and on window focus"). */
const SUMMARY_REFRESH_MS = 60_000;

export function useNotificationSummaryQuery() {
  return useQuery({
    queryKey: ['notifications', 'summary'],
    queryFn: fetchNotificationSummary,
    refetchInterval: SUMMARY_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useNotificationsQuery(unreadOnly: boolean) {
  return useInfiniteQuery({
    queryKey: ['notifications', 'list', unreadOnly],
    queryFn: ({ pageParam }) => fetchNotificationsPage(pageParam, unreadOnly),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    retry: false,
  });
}

/** Marking one or all read invalidates both the list and the bell's summary. */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}
